import { useState, useEffect, useMemo, useRef } from "react";
import { useRecoilState, useRecoilValue } from "recoil";
import { HiDotsVertical, HiOutlineLink, HiOutlinePencil, HiOutlineTrash } from "react-icons/hi";
import { RiSendPlaneLine } from "react-icons/ri";
import { FaWhatsapp } from "react-icons/fa";
import { getLatestTest, isTestFinished, runTestLinkAction, TEST_LINK_ACTIONS } from "../../../../utils/test-invitation-link";
import { userAtom } from "../../../../services/atoms/auth.atoms";
import TestStatusBadge from "../../../components/test-status-badge/test-status-badge";
import { AiOutlineBell, AiOutlineClose, AiOutlineUser, AiOutlineCloudDownload, AiOutlineMail } from "react-icons/ai";
import SendFromMyEmailModal from "../../../components/send-from-my-email/send-from-my-email-modal";
import { MdArrowForwardIos } from "react-icons/md";
import { HiMagnifyingGlass } from "react-icons/hi2";
import { find, includes, map, remove } from "lodash";
import { toast } from "react-hot-toast";

import BulkLoadModal from "./bulk-load-modal";
import InvitationModal from "./invitation-modal";
import EditCollaboratorModal from "../../collaborator-details/components/edit-modal";
import CreateCollaboratorModal from "../../collaborator-details/components/create-modal";
import TableComponent from "../../../../components/table/table-component";
import { collaboratorsAtom } from "../../../../services/atoms/collaborators.atoms";
import { useCollaboratorsActions } from "../../../../services/actions/collaborators.actions";
import { ButtonLoading } from "../../../../components/buttons/button-loading";
import { ButtonIcon } from "../../../../components/buttons/button-icon";
import { InputIcon } from "../../../../components/inputs/input-icon";
import { ROUTES } from "../../../../helpers/constants";
import { formatRut } from "rutlib/lib";
import BulkLoadErrors from "./bulk-load-modal-errors";
import CreateUserAdminModal from "../../usersadmin/components/create-modal";
import UsersAdmin from "../../usersadmin";
import { useUsersAdminAction } from "../../../../services/actions/users-admin.actions";
import { usersAdminAtom } from "../../../../services/atoms/users-admin.atom";
import ColorsCircles from "../../params/components/colors-roles";
import { colorsFromResults } from "../../../../helpers/functions";
import { TestTypeService } from "../../../../utils/test-type-registry";
import SortDropdown from "../../../../components/dropdown/sort-dropdown";
import ConfirmDialog from "../../../../components/confirm-dialog/confirm-dialog";

const HIDE_DISC = true;

// Reenviar test / Copiar link / WhatsApp: actúan sobre el test vigente (el más
// reciente) de la persona. Si el correo no le llegó, el cliente lo resuelve aquí.
const options = [
  { id: 'opt-6', title: 'Enviar invitación', action: 'my-email', icon: AiOutlineMail },
  { id: 'opt-7', title: 'Enviar recordatorio', action: 'my-email-reminder', icon: AiOutlineBell },
  { id: 'opt-3', title: 'Reenviar test', action: 'resend', icon: RiSendPlaneLine },
  { id: 'opt-4', title: 'Copiar link del test', action: 'copy-link', icon: HiOutlineLink },
  { id: 'opt-5', title: 'Enviar por WhatsApp', action: 'whatsapp', icon: FaWhatsapp },
  { id: 'opt-1', title: 'Editar Colaborador', action: 'edit', icon: HiOutlinePencil },
  { id: 'opt-2', title: 'Eliminar Colaborador', action: 'remove', icon: HiOutlineTrash },
];

const badgeAccessor = (row) => (<div className='gm-badge'><span>{row || 'No informada'}</span> </div>);

const findLatestFinishedTest = (tests, isTestGrowmeResult = false) => {
  if (isTestGrowmeResult) {
    return tests.find((test) => test.statusId === 4 && TestTypeService.isTestCategory(test, 'competency'));
  }
  
  return tests.find((test) => test.statusId === 4 && TestTypeService.isTestCategory(test, 'disc'));
};

const renderColorCircles = (colorsForCircles) => (
  <div style={{ display: 'flex', flexDirection: 'row', justifyContent: "center" }}>
    {colorsForCircles.map((colors, index) => (
      <div key={`${index}-${colors}`}>
        <ColorsCircles colors={colors} />
      </div>
    ))}
  </div>
);

const renderEmptyColorCircles = () => (
  <div style={{ display: 'flex', flexDirection: 'row', justifyContent: "center" }}>
    <ColorsCircles /> <ColorsCircles />
  </div>
);

const findLatestUnifiedTest = (tests) => {
  return tests.find((test) => test.statusId >= 4 && TestTypeService.isTestCategory(test, 'unified'));
};

const unifiedTestColorAccessor = (row) => {
  const latestUnifiedTest = findLatestUnifiedTest(row);
  if (!latestUnifiedTest) {
    return renderEmptyColorCircles();
  }
  const colorsForCircles = colorsFromResults(latestUnifiedTest.results, false, true);
  return renderColorCircles(colorsForCircles);
};

const combinedGrowmeAccessor = (row) => {
  const latestUnified = findLatestUnifiedTest(row);
  const latestCompetency = findLatestFinishedTest(row, true);
  const testToUse = latestUnified || latestCompetency;
  if (!testToUse) {
    return renderEmptyColorCircles();
  }
  const colorsForCircles = colorsFromResults(testToUse.results, false, true);
  return renderColorCircles(colorsForCircles);
};

const resultsColorAccessor = (row, isTestGrowmeResult = false) => {
  const isBoundedDisc = !isTestGrowmeResult && row.length === 2;
  const latestFinishedTest = findLatestFinishedTest(row, isTestGrowmeResult);
  if (!latestFinishedTest) {
    return renderEmptyColorCircles();
  }
  const colorsForCircles = colorsFromResults(
    latestFinishedTest.results,
    isBoundedDisc,
    isTestGrowmeResult
  );
  return renderColorCircles(colorsForCircles);
};

// Invitación / Recordatorio (desde el correo de quien está en el panel) / Copiar link a
// la vista en cada fila, solo si la persona tiene un test pendiente. "Reenviar test" y
// "Enviar por WhatsApp" quedan en el menú de la fila. stopPropagation: el clic en la fila abre la ficha.
const testActionsAccessor = (row, onTestAction) => {
  const latest = getLatestTest(row.latestTest);
  if (!latest?.id || isTestFinished(latest)) return <div>-</div>;
  const run = (action) => (event) => { event.stopPropagation(); onTestAction(action, row); };
  return (
    <div className="gm-row-test-actions">
      <button type="button" className="gm-row-test-actions-primary" title="Enviar la invitación al test desde tu correo" onClick={run('my-email')}>
        <AiOutlineMail size={14} /> Enviar invitación
      </button>
      <button type="button" title="Enviar un recordatorio desde tu correo" onClick={run('my-email-reminder')}>
        <AiOutlineBell size={14} /> Recordatorio
      </button>
      <button type="button" title="Copiar link del test" onClick={run('copy-link')}>
        <HiOutlineLink size={14} />
      </button>
    </div>
  );
};

const buildColumns = (onTestAction) => [
  { Header: 'Nombre Completo', accessor: ({ name, surname }) => <div>{`${name} ${surname}`}</div> },
  // Estado y acciones del test junto al nombre: a la vista sin desplazar la tabla.
  // latestTest trae todos los tests de la persona; el badge muestra el más reciente.
  { Header: 'Estado del test', id: 'test-status', accessor: ({ latestTest }) => <TestStatusBadge tests={latestTest} />, disableSortBy: true },
  { Header: 'Test', id: 'test-actions', accessor: (row) => testActionsAccessor(row, onTestAction), disableSortBy: true },
  { Header: 'Correo', accessor: ({ email }) => <div>{email}</div> },
  { Header: 'Empresa', accessor: ({ company }) => <div>{company || 'No informada'}</div> },
  { Header: 'Cargo a postular', accessor: ({ position }) => badgeAccessor(position) },
  // { Header: 'Área', accessor: 'area', disableFilters: false },
  { Header: 'Área', accessor: ({ area }) => <div>{area || 'No informada'}</div> },
  { Header: 'Origen', accessor: ({ collaboratorInternal }) => <div>{collaboratorInternal ? 'Interno' : 'Externo'}</div> },
  ...(!HIDE_DISC ? [
    { Header: 'Test Disc', accessor: ({ latestTest }) => resultsColorAccessor(latestTest)},
  ] : []),

  // New unified test should be shown as 'Test Growme'
  // If both unified and legacy Growme exist, show unified; otherwise show whichever is available
  { Header: 'Test Growme', accessor: ({ latestTest }) => combinedGrowmeAccessor(latestTest) },
  { Header: 'Acciones', accessor: '', disableFilters: true, disableSortBy: true, type: 'actions' },
];

const initialCollaboratorData = {
  id: '',
  name: "",
  surname: "",
  rut: "",
  email: "",
  area: "",
  position: "",
}

const initialSetOpenState = {
  bulk: false,
  invitation: false,
  create: false,
  edit: false,
  errors: false,
  createUserAdmin: false,
  editUserAdmin: false
};

// Espera tras la última tecla antes de buscar, para no pedir al back en cada letra.
const SEARCH_DEBOUNCE_MS = 400;

// El back busca 'collaborator.all' en nombre, apellido, RUT (con o sin puntos/guion),
// correo, cargo, área, empresa y nombre de los tests; se combina con el filtro de origen.
const buildSearch = (term, origin) => {
  const search = [];
  if (term) search.push({ column: 'collaborator.all', value: term });
  if (origin) search.push({ column: `collaborator.${origin}`, value: true });
  return search.length ? search : null;
};

// Las claves deben coincidir con COLLABORATORS_SORT_OPTIONS del back
// (TypeOrmCollaboratorRepository).
const sortOptions = [
  { value: 'created_recent', label: 'Creados más recientes' },
  { value: 'created_oldest', label: 'Creados más antiguos' },
  { value: 'recent', label: 'Test enviado más reciente' },
  { value: 'oldest', label: 'Test enviado más antiguo' },
  { value: 'finished_recent', label: 'Último test finalizado' },
  { value: 'name_asc', label: 'Nombre (A - Z)' },
  { value: 'name_desc', label: 'Nombre (Z - A)' },
];
const SORT_STORAGE_KEY = 'gm-collaborators-sort';
const DEFAULT_SORT = 'name_asc';

const getStoredSort = () => {
  try {
    const stored = localStorage.getItem(SORT_STORAGE_KEY);
    return sortOptions.some((option) => option.value === stored) ? stored : DEFAULT_SORT;
  } catch {
    return DEFAULT_SORT;
  }
};

const TableSection = ({ navigate, client, onTestSent }) => {
  const {
    resetCollaboratorsList,
    updateCollaborator,
    collaboratorsList,
    downloadReports,
    resetInvitation,
    resetBulkLoad,
    deleteCollaborator,
    resendInvitation
  } = useCollaboratorsActions();
  const {usersAdminsList} = useUsersAdminAction()
  const [usersAdmin, setUsersAdmin] = useRecoilState(usersAdminAtom);
  const collaborators = useRecoilValue(collaboratorsAtom);
  const user = useRecoilValue(userAtom);
  // Las columnas se arman una sola vez (react-table las necesita estables); la acción
  // del test pasa por una ref para usar siempre el resendInvitation vigente.
  const testActionRef = useRef(null);
  // Ventana "Enviar invitación / recordatorio" (null = cerrada).
  const [myEmailTarget, setMyEmailTarget] = useState(null);
  testActionRef.current = (action, row) =>
    runTestLinkAction({ action, tests: row?.latestTest, name: row?.name, phone: row?.phone, email: row?.email, resend: resendInvitation, openMyEmail: setMyEmailTarget });
  const columns = useMemo(() => buildColumns((action, row) => testActionRef.current(action, row)), []);
  const [collaborator, setCollaborator] = useState(initialCollaboratorData);
  const [checkedItems, setCheckItem] = useState([]);
  const [allItemsChecked, setAllItemsChecked] = useState(false);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [appliedTerm, setAppliedTerm] = useState('');
  const [origin, setOrigin] = useState('');
  const [open, setOpen] = useState(initialSetOpenState);
  const [isInternal, setInternal] = useState(true)
  const [viewSelected, setViewSelected] = useState('collaborators')
  const [errorsModal, setErrorsModal] = useState([])
  const [sort, setSort] = useState(getStoredSort);
  // Colaborador que se está por eliminar (null = diálogo cerrado).
  const [pendingDelete, setPendingDelete] = useState(null);
  const hasActiveSearch = Boolean(searchTerm || appliedTerm || origin);

  // Recarga la lista respetando el orden elegido y la búsqueda/filtro activo.
  const refreshCollaborators = () =>
    collaboratorsList({ page, limit, clientId: client.id, search: buildSearch(appliedTerm, origin), sort });

  useEffect(() => {
    refreshCollaborators();
    // eslint-disable-next-line
  }, [page, limit, sort, appliedTerm, origin]);

  // Busca mientras se escribe (con debounce). Cada búsqueda nueva vuelve a la
  // página 1: antes se buscaba en la página actual y podía no mostrar nada.
  const applySearch = (term) => {
    setAppliedTerm(term);
    setPage(1);
  };

  useEffect(() => {
    const term = searchTerm.trim();
    if (term === appliedTerm) return;
    const timer = setTimeout(() => applySearch(term), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
    // eslint-disable-next-line
  }, [searchTerm]);

  useEffect(() => {
    return () => resetCollaboratorsList()
    // eslint-disable-next-line
  }, []);

  const handleChangeSort = (value) => {
    setSort(value);
    setPage(1);
    try {
      localStorage.setItem(SORT_STORAGE_KEY, value);
    } catch {
      // Sin acceso a localStorage: el orden se aplica igual, solo no se recuerda.
    }
  };

  useEffect(() => {
    !open.bulk && resetBulkLoad();
    !open.invitation && resetInvitation();
    // eslint-disable-next-line
  }, [open]);

  useEffect(()=>{
  usersAdminsList(client.name)
  }, [])


  const handleChangePage = (e) => setPage(e + 1);
  const handleSetLimit = (e) => setLimit(e);
  const handleOpenDetails = (e, index) => {
    navigate(`${ROUTES.CLIENTS.route}/collaborator/${e.id}`, { state: { collaborator: e, client, index: `${index}` } });
  }
  const handleOptionAction = (action, row) => {
    if (action === 'edit') {
      setCollaborator(row);
      setOpen(modal => ({ ...modal, edit: true }))
    };
    if(action==='remove'){
      setCollaborator(row)
      handleDeleteCollaborator(row)
    }
    if (TEST_LINK_ACTIONS.includes(action)) {
      testActionRef.current(action, row);
    }
  };

  const actions = [
    { icon: HiDotsVertical, dropdown: true },
    { icon: MdArrowForwardIos, action: handleOpenDetails },
  ];

  const downloadReportsAction = () => {
    toast('Preparando descarga...', {
      icon: '😎',
    });
    downloadReports({
      collaboratorsIds: checkedItems.map(i => i.id),
    }, ({ signedUrl, testsFinished }) => {
      if (testsFinished) {
        toast.success(`Pruebas encontradas ${testsFinished}`);
        window.open(signedUrl, '_blank');
      } else {
        toast('Aun no hay pruebas finalizadas!', {
          icon: '😱',
        });
      }
    });
  };

  const handleOpenModal = (key) => {
    if (key === 'invitation' && !checkedItems.length) {
      return toast('Debe seleccionar al menos un colaborador.', {icon: '🎯'});
    }
    setOpen(modal => ({ ...modal, [key]: !modal[key] }));
  };

  // Enter busca al instante, sin esperar el debounce.
  const handleSubmitSearch = (e) => {
    e.preventDefault();
    const term = searchTerm.trim();
    if (term !== appliedTerm) applySearch(term);
  };

  const handleChangeOrigin = (event) => {
    setOrigin(event.target.value);
    setPage(1);
  };

  const handleCleanSearch = () => {
    setSearchTerm('');
    setAppliedTerm('');
    setOrigin('');
    setPage(1);
  };

  const handleAllChecked = (event) => {
    setAllItemsChecked(event.target.checked);
    let collabs = collaborators?.payload || [];
    event.target.checked ?
      setCheckItem(map(collabs, (e) => ({ ...e, checked: true }))) :
      setCheckItem([]);
  }

  const handleCheckOne = (checked, row) => {
    setAllItemsChecked(false);
    let items = [...checkedItems];
    find(items, e => e.id === row.id) ? remove(items, (e) => e.id === row.id) : items.push({ ...row, checked });
    setCheckItem(items);
  }

  const handleSubmitEdit = (e) => {
    e.preventDefault();
    const phoneNumber = collaborator?.phone
    const dataToSend={
      ...collaborator,
      collaboratorInternal: isInternal,
      collaboratorExternal: !isInternal, 
      rut: formatRut(collaborator.rut),
      phone: phoneNumber == undefined ? "" : phoneNumber
    }
    updateCollaborator(dataToSend, () => {
      toast.success('Colaborador actualizado exitosamente');
      handleOpenModal('edit');
      refreshCollaborators();
    });
  }

  // Mismo flujo de antes (mismos avisos y recarga), con un diálogo que no se cierra
  // solo. Si el back falla, deleteCollaborator ya avisa el error y el diálogo sigue
  // abierto para reintentar o cancelar.
  const handleConfirmDelete = async () => {
    const row = pendingDelete;
    if (!row) return;
    await deleteCollaborator(row.id, (res) => {
      if (includes(res?.message, 'Request failed with status')) {
        toast.error('El colaborador no se ha podido eliminar', {duration:4000});
      } else {
        toast.success('Colaborador eliminado exitosamente', {duration:4000});
        refreshCollaborators();
      }
      setPendingDelete(null);
    });
  };

  const handleDeleteCollaborator = (row) => setPendingDelete(row);

  const clearSelection = () => {
    setCheckItem([]);
    setAllItemsChecked(false);
  };

  return (
    <div className="gm-table-section-container">
      {open.errors && (
        <BulkLoadErrors
        open={open.errors}
        toggle={() => handleOpenModal("errors")}
        errorsModal={errorsModal}
        />
      )}
      {open.edit && (
        <EditCollaboratorModal
          collaboratorHandler={{
            collaborator,
            setCollaborator,
            isInternal,
            setInternal,
          }}
          open={open.edit}
          submit={handleSubmitEdit}
          toggle={() => handleOpenModal("edit")}
          clientId={client?.id || ""}
        />
      )}
      {open.bulk && (
        <BulkLoadModal
          open={open.bulk}
          toggle={() =>{
            handleOpenModal("bulk")
          } }
          clientId={client?.id || ""}
          update={()=>{refreshCollaborators();}}
          pageMeta={{ page, limit }}
          handleOpenModal={()=>handleOpenModal('errors')}
          setErrorsModal={setErrorsModal}
        />
      )}
      {open.invitation && (
        <InvitationModal
          open={open.invitation}
          toggle={() => handleOpenModal("invitation")}
          checkedItems={checkedItems}
          allItems={collaborators?.payload}
          onTestSent={onTestSent}
        />
      )}
      {open.create && (
        <CreateCollaboratorModal
          open={open.create}
          toggle={() => handleOpenModal("create")}
          clientId={client?.id || ""}
          update={() => refreshCollaborators()}
          handleOpenModal={handleOpenModal}
          bulkLoad
        />
      )}
      {open.createUserAdmin && (
        <CreateUserAdminModal
          open={open.createUserAdmin}
          toggle={() => handleOpenModal("createUserAdmin")}
          clientId={client?.id || ""}
          clientName={client.name || ""}
        />
      )}
      {/* <div className="gm-ts-title">Colaboradores</div> */}
      <div className="gm-ts-searchbar-container">
        <div className="gm-ts-searchbar-title-container">
          <div className="gm-ts-searchbar-title">
            <button
              className={viewSelected === 'collaborators' ? "gm-ts-searchbar-button" : 'gm-ts-searchbar-button-inactive'}
              onClick={() => {
                setViewSelected("collaborators");
              }}
            >{`Colaboradores (${collaborators?.total || 0})`}</button>
          </div>
          <div className="gm-ts-searchbar-title">
            <button
              className={viewSelected === 'users' ? "gm-ts-searchbar-button" : 'gm-ts-searchbar-button-inactive'}
              onClick={() => {
                setViewSelected("users");
              }}
            >{`Usuarios (${usersAdmin?.payload.length})`}</button>
          </div>
        </div>

        {viewSelected === "collaborators" && (
          <div className="gm-ts-searchbar-actions-container">
            {hasActiveSearch && (
              <button
                className="gm-clean-search-button"
                onClick={handleCleanSearch}
                type="button"
                title="Limpiar búsqueda"
                aria-label="Limpiar búsqueda"
              >
                <AiOutlineClose size={18} />
              </button>
            )}
            <form onSubmit={handleSubmitSearch} role="search">
              <InputIcon
                Icon={HiMagnifyingGlass}
                type="search"
                placeholder="Buscar por nombre, RUT, correo, empresa, cargo o área"
                title="Busca por nombre, RUT (con o sin puntos), correo, cargo, área, empresa o test. Puedes combinar palabras."
                aria-label="Buscar colaboradores"
                name="search"
                autoComplete="off"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyDown={(e) => e.key === "Escape" && handleCleanSearch()}
              />
            </form>
            <form>
              <div className="gm-input-icon-container">
                <select
                  name="origen"
                  value={origin}
                  onChange={handleChangeOrigin}
                >
                  <option value="">Todos los orígenes</option>
                  <option value="collaboratorInternal">Interno</option>
                  <option value="collaboratorExternal">Externo</option>
                </select>
              </div>
            </form>
            <SortDropdown
              options={sortOptions}
              value={sort}
              onChange={handleChangeSort}
              disabled={collaborators.loading}
            />
            <ButtonIcon
              Icon={AiOutlineUser}
              buttonLabel="Cargar Colaboradores"
              type="button"
              className="gm-outline-button"
              onClick={() => handleOpenModal("create")}
            />
            {checkedItems.length > 0 && (
              <ButtonIcon
                Icon={AiOutlineCloudDownload}
                buttonLabel="Descargar Informes"
                type="button"
                className="gm-outline-button"
                onClick={() => downloadReportsAction()}
              />
            )}
            <ButtonLoading
              className="gm-primary-button"
              type="button"
              buttonLabel="Enviar Invitación"
              onClick={() => handleOpenModal("invitation")}
              disabled={!collaborators.payload?.length || collaborators.loading}
              loading={collaborators.loading}
            />
          </div>
        )}

        {viewSelected === "users" && (
          <div className="gm-ts-searchbar-actions-container">
            {/* Sin buscador: el de colaboradores no filtra usuarios y confundía. */}
            <ButtonIcon
              Icon={AiOutlineUser}
              buttonLabel="Crear Usuario"
              type="button"
              className="gm-outline-button"
              onClick={() => handleOpenModal("createUserAdmin")}
            />
          </div>
        )}
      </div>
      <ConfirmDialog
        open={Boolean(pendingDelete)}
        title={`¿Eliminar a ${[pendingDelete?.name, pendingDelete?.surname].filter(Boolean).join(' ') || 'este colaborador'}?`}
        description="Se eliminará de la lista de colaboradores. Esta acción no se puede deshacer."
        confirmLabel="Eliminar"
        onConfirm={handleConfirmDelete}
        onCancel={() => setPendingDelete(null)}
      />
      {viewSelected === "collaborators" && checkedItems.length > 0 && (
        <div className="gm-bulk-bar" role="region" aria-label="Acciones sobre los colaboradores seleccionados">
          <span className="gm-bulk-count">
            {checkedItems.length} {checkedItems.length === 1 ? 'seleccionado' : 'seleccionados'}
          </span>
          <button type="button" className="gm-bulk-ghost" onClick={() => downloadReportsAction()}>
            <AiOutlineCloudDownload size={16} /> Descargar informes
          </button>
          <button type="button" className="gm-primary-button" onClick={() => handleOpenModal("invitation")}>
            <RiSendPlaneLine size={16} /> Enviar invitación
          </button>
          <button type="button" className="gm-bulk-close" onClick={clearSelection} title="Quitar selección" aria-label="Quitar selección">
            <AiOutlineClose size={16} />
          </button>
        </div>
      )}
      <SendFromMyEmailModal target={myEmailTarget} onClose={() => setMyEmailTarget(null)} onSent={refreshCollaborators} />
      {viewSelected === "collaborators" && (
        <TableComponent
          // La tabla guarda su pageIndex internamente: al cambiar el orden o la
          // búsqueda se remonta para volver a la página 1 junto con el estado `page`.
          key={`${sort}|${appliedTerm}|${origin}`}
          data={collaborators?.payload || []}
          loading={collaborators.loading}
          emptyListInformer="small"
          columns={columns}
          // Cada usuario elige qué columnas ver (botón + al final de la cabecera).
          columnSettingsKey={`clientes-colaboradores:${user?.data?.id || user?.data?.email || 'anonimo'}`}
          handleOptionAction={handleOptionAction}
          handleOpenDetails={handleOpenDetails}
          handleChangePage={handleChangePage}
          handleSetLimit={handleSetLimit}
          controlledPageCount={
            collaborators?.total ? Math.ceil(collaborators.total / limit) : 1
          }
          actions={actions}
          options={options}
          section={{ plural: "colaboradores", singular: "colaborador" }}
          checkbox={true}
          itemChecked={checkedItems}
          allItemsChecked={allItemsChecked}
          handleCheckOne={handleCheckOne}
          handleAllChecked={handleAllChecked}
          manualPagination={true}
          pagination
        />
      )}

      {
        viewSelected === "users" && (
          <UsersAdmin
            clientName={client.name || ""}
          />
        )
      }
    </div>
  );
}

export default TableSection;